import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { FixedSizeList as List } from "react-window";

import { defined } from "@formant/core/common/defined";
import { duration } from "@formant/core/model/duration";
import { IChannel } from "@formant/core/model/IChannel";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { IEventCountsByDevice } from "@formant/core/model/IEventCountsByDevice";
import { IEventFilter } from "@formant/core/model/IEventFilter";
import {
    Badge,
    // Button,
    ChevronDown,
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
    Hash,
    LayoutGrid,
    // Plus,
    Router,
    SidebarContent,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    Waypoints,
} from "@formant/ui-kit";
import { cn } from "@formant/ui-kit/src/lib/utils";

import { useStores } from "../../context/StoreContext";
import {
    authStore,
    eventStore,
    organizationStore,
    routerStore,
} from "../../stores";
import { FleetEventBadge } from "./fleet-event-badge";
import {
    compareVariantAndReturnMostSevere,
    FleetEventBadgePassive,
    getVariant,
    VariantType,
} from "./fleet-event-badge-passive";
import { ITagsAndGroups } from "./fleet-sidebar.types";

export interface INewSidebarProps {
    handleSideBarClose: () => void;
}
interface IDevice {
    id: string;
    name: string;
    to: string;
    testId: string;
    isOnline?: boolean;
    badgeContent?: React.ReactNode;
}

interface IFleet {
    id: string;
    name: string;
    devicesStatus: string;
    to?: string;
    icon: React.ReactNode;
    testId?: string;
    badge?: {
        count: number;
        variant: VariantType;
    };
    badgeContent?: React.ReactNode;
    devices: IDevice[];
}

interface IProps {
    onClose: () => void;
    tagsAndGroups: ITagsAndGroups[];
    selectedOption: string | undefined;
    channels: IChannel[];
    devicesData: IDeviceDetails[];
}

export const FleetSidebarContent = ({
    onClose,
    selectedOption,
    tagsAndGroups,
    channels,
    devicesData,
}: IProps) => {
    const [openFleets, setOpenFleets] = useState<string[]>([]);
    const [openGroups, setOpenGroups] = useState<string[]>([]);
    const [deviceCounts, setDeviceCounts] = useState<
        IEventCountsByDevice | undefined
    >();
    const { routeTo, navigate } = routerStore;
    const { onlineDevicesStore } = useStores();

    const isKiosk = authStore.hasAccess("kiosk");
    const hideRobotList = false;

    const sidebarRef = useRef<HTMLDivElement>(null);
    const fleetRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const navigateAndClose = (to: string) => {
        navigate(to).catch(err => console.error(err));
        onClose();
    };

    useEffect(() => {
        let timer: any;
        if (!isKiosk && !hideRobotList) {
            const fetchDeviceCounts = () => {
                if (!devicesData.length) {
                    return;
                }
                const allDevicesFilter: IEventFilter = {
                    deviceIds: devicesData.map(_ => _.id).sort(),
                    disableNullMatches: true,
                    viewed: false,
                    notificationEnabled: true,
                    setsDeviceColor: true,
                };
                setDeviceCounts(eventStore.countsByDevice(allDevicesFilter));
            };
            if (devicesData.length > 0) {
                fetchDeviceCounts();
            }

            timer = setInterval(
                () => fetchDeviceCounts(),
                15 * duration.second,
            );
        }

        return () => {
            clearInterval(timer);
        };
    }, [devicesData.length, hideRobotList, isKiosk, devicesData]);

    const organization = organizationStore.authenticated();

    const fleetList: IFleet[] = [];
    const groupedFleetList: {
        data: IFleet[];
        name: string;
        badge: {
            count: number;
            variant: "info" | "warning" | "error" | "critical" | "open";
        };
    }[] = [];
    const online = onlineDevicesStore.online();

    if (!isKiosk && !hideRobotList) {
        const deviceFleet: IFleet = {
            id: "devices",
            name: (organization && organization.name) || "Devices",
            devicesStatus: "",
            to: routeTo.home(),
            badge: { count: 0, variant: "info" },
            devices: [],
            icon: <Waypoints className="h-4 w-4" />,
        };
        let onlineCount = 0;
        devicesData.forEach(device => {
            const id = defined(device.id);
            const deviceCount = deviceCounts && deviceCounts.counts[id];
            const isOnline = online?.has(id) || false;
            if (isOnline) {
                onlineCount++;
            }

            defined(deviceFleet.badge).count +=
                (deviceCount?.["triggered-event"] || 0) +
                (deviceCount?.["intervention-request"] || 0) +
                (deviceCount?.custom || 0) +
                (deviceCount?.stateful || 0);

            const variant = getVariant(deviceCount);

            defined(deviceFleet.badge).variant =
                compareVariantAndReturnMostSevere(
                    variant,
                    deviceFleet.badge?.variant || "info",
                );

            deviceFleet.devices.push({
                id,
                name: device.name,
                isOnline,
                testId: `sidebar-device-${id}`,
                to: routeTo.device({ id }),
                badgeContent: (
                    <FleetEventBadgePassive
                        counts={deviceCount}
                        onClick={() =>
                            navigateAndClose(
                                routeTo.unifiedDevice({ mode: "events", id }),
                            )
                        }
                    />
                ),
            });
        });
        deviceFleet.devicesStatus = `${onlineCount}/${deviceFleet.devices.length} devices online`;
        fleetList.push(deviceFleet);
    }

    const convertToFleetItem = (tagItem: ITagsAndGroups) => {
        const result: IFleet[] = [];
        tagItem.groups.forEach(group => {
            let onlineCount = 0;
            const groupFleet: IFleet = {
                id: group.id || "",
                name: group.name,
                devicesStatus: `${group.devices?.length || 0} devices`,
                icon: <LayoutGrid className="h-4 w-4" />,
                to: routeTo.smartFleet({
                    id: defined(group.id),
                }),
                testId: `group-key-${group.id}`,
                badge: { count: 0, variant: "info" },
                devices: [],
            };
            if (group.devices) {
                groupFleet.devices =
                    group.devices.map(device => {
                        const id = defined(device.id);
                        const isOnline = online?.has(id) || false;
                        if (isOnline) {
                            onlineCount++;
                        }
                        const deviceCount =
                            deviceCounts && deviceCounts.counts[id];
                        defined(groupFleet.badge).count +=
                            (deviceCount?.["triggered-event"] || 0) +
                            (deviceCount?.["intervention-request"] || 0) +
                            (deviceCount?.custom || 0) +
                            (deviceCount?.stateful || 0);
                        const variant = getVariant(deviceCount);
                        if (groupFleet.badge) {
                            groupFleet.badge.variant =
                                compareVariantAndReturnMostSevere(
                                    variant,
                                    groupFleet.badge?.variant || "info",
                                );
                        }
                        return {
                            id: `sidebar-group-device-${tagItem.tag.tagKey}-${group.name}-${id}`,
                            name: device.name,
                            isOnline,
                            testId: `sidebar-group-device-${tagItem.tag.tagKey}-${group.name}-${id}`,
                            to: routeTo.device({ id }),
                            badgeContent: (
                                <FleetEventBadgePassive
                                    counts={deviceCount}
                                    onClick={() =>
                                        navigateAndClose(
                                            routeTo.unifiedDevice({
                                                mode: "events",
                                                id,
                                            }),
                                        )
                                    }
                                />
                            ),
                        };
                    }) || [];
            }
            groupFleet.devicesStatus = `${onlineCount}/${groupFleet.devices.length} devices online`;
            result.push(groupFleet);
        });
        return result;
    };

    if (selectedOption === "Channels") {
        channels.forEach(channel => {
            const id = defined(channel.id);
            const channelEventFilter = {
                ...channel.filter,
                viewed: false,
                disableNullMatches: true,
                notificationEnabled: true,
            };
            const channelFleet: IFleet = {
                id: channel.id || "",
                name: channel.name,
                devicesStatus: "",
                to: routeTo.channel({ id }),
                testId: `sidebar-channel-${id}`,
                icon: <Hash className="h-4 w-4" />,
                devices: [],
                badgeContent: (
                    <FleetEventBadge
                        filter={channelEventFilter}
                        onClick={() =>
                            navigateAndClose(routeTo.channelEvents({ id }))
                        }
                    />
                ),
            };
            fleetList.push(channelFleet);
        });
    } else {
        const tagItem = tagsAndGroups.find(
            x => x.tag.tagKey === selectedOption,
        );
        if (tagItem) {
            const fleetItems = convertToFleetItem(tagItem);
            fleetList.push(...fleetItems);
        } else {
            tagsAndGroups.forEach(item => {
                const fleetItems = convertToFleetItem(item);
                const totalCount = fleetItems.reduce(
                    (acc, fleet) => acc + (fleet.badge?.count || 0),
                    0,
                );
                let variant: "info" | "warning" | "error" | "critical" = "info";
                if (totalCount > 0) {
                    fleetItems.forEach(fleet => {
                        if (fleet.badge) {
                            variant = compareVariantAndReturnMostSevere(
                                variant,
                                fleet.badge.variant,
                            );
                        }
                    });
                }
                groupedFleetList.push({
                    data: fleetItems,
                    name: item.tag.tagKey,
                    badge: { count: totalCount, variant },
                });
            });
        }
    }

    const DeviceItem = React.memo(({ index, style, data }: any) => {
        const device = data.devices[index];
        const textRef = React.useRef<HTMLSpanElement>(null);
        const [isTextTruncated, setIsTextTruncated] = React.useState(false);

        React.useEffect(() => {
            const checkTruncation = () => {
                if (textRef.current) {
                    setIsTextTruncated(
                        textRef.current.scrollWidth >
                            textRef.current.clientWidth,
                    );
                }
            };

            checkTruncation();
            // Add resize observer to check truncation on resize
            const resizeObserver = new ResizeObserver(checkTruncation);
            if (textRef.current) {
                resizeObserver.observe(textRef.current);
            }

            return () => resizeObserver.disconnect();
        }, [device.name]);

        return (
            <div
                style={style}
                key={device.id}
                onClick={e => {
                    e.stopPropagation();
                    data.navigateAndClose(device.to);
                }}
                data-testid={device.testId}
                className="flex relative items-center justify-between cursor-pointer rounded-lg p-2 hover:bg-formant-color-background-secondary"
            >
                <div
                    className={`absolute top-1/2 left-0 -translate-y-1/2 w-[0.25rem] h-[1.25rem] rounded-r-[0.25rem] mb-4 ${
                        device.isOnline
                            ? "bg-formant-highlights-blue"
                            : "bg-transparent"
                    }`}
                />
                <div className="flex items-center gap-2 pl-2">
                    <Router className="h-4 w-4 text-formant-color-text-secondary" />
                    <TooltipProvider
                        delayDuration={150}
                        skipDelayDuration={150}
                    >
                        {isTextTruncated ? (
                            <Tooltip>
                                <TooltipTrigger asChild={true}>
                                    <span
                                        ref={textRef}
                                        className="whitespace-nowrap max-w-[11rem] overflow-hidden text-ellipsis text-formant-color-text-primary"
                                        onClick={e => {
                                            e.stopPropagation();
                                            data.navigateAndClose(device.to);
                                        }}
                                    >
                                        {device.name}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{device.name}</p>
                                </TooltipContent>
                            </Tooltip>
                        ) : (
                            <span
                                ref={textRef}
                                className="whitespace-nowrap max-w-[11rem] overflow-hidden text-ellipsis text-formant-color-text-primary"
                                onClick={e => {
                                    e.stopPropagation();
                                    data.navigateAndClose(device.to);
                                }}
                            >
                                {device.name}
                            </span>
                        )}
                    </TooltipProvider>
                </div>
                {device.badgeContent}
            </div>
        );
    });

    const renderFleet = (fleet: IFleet, isNested = false) => {
        const itemHeight = 40;
        const calculateListHeight = (
            deviceCount: number,
            curItemHeight: number,
        ) => {
            const maxItems = deviceCount > 10 ? 10 : deviceCount;

            const maxDevicesHeight = maxItems * curItemHeight;

            return maxDevicesHeight;
        };

        return (
            <Collapsible
                key={fleet.id}
                className={cn(
                    "bg-formant-color-background-active mb-2 p-2 collapsible !cursor-pointer rounded-lg",
                    openFleets.includes(fleet.id)
                        ? ""
                        : "hover:bg-formant-color-background-active-2",
                    isNested && "mb-2 p-2",
                )}
                open={openFleets.includes(fleet.id)}
                onOpenChange={isOpen =>
                    setOpenFleets(
                        isOpen
                            ? [...openFleets, fleet.id]
                            : openFleets.filter(id => id !== fleet.id),
                    )
                }
            >
                <div
                    className="flex w-full items-center justify-between rounded-lg p-2  bg-transparent"
                    ref={el => (fleetRefs.current[fleet.id] = el)}
                    onClick={() => fleet.to && navigateAndClose(fleet.to)}
                >
                    <div className="">
                        <div className="flex flex-col cursor-pointer">
                            <div className="flex items-center gap-2 w-max text-formant-color-text-primary">
                                {fleet.icon}
                                <span className="truncate max-w-[11rem]">
                                    {fleet.name}
                                </span>
                            </div>
                            <CollapsibleTrigger
                                onClick={e => {
                                    e.stopPropagation();
                                }}
                                asChild={true}
                            >
                                {fleet.devicesStatus && (
                                    <div className=" small-dropdown flex items-center gap-1 text-sm  text-formant-color-text-primary  hover:bg-formant-color-background-active rounded-lg p-1">
                                        <ChevronDown
                                            className={cn(
                                                "h-4 w-4 transition-transform duration-200",
                                                openFleets.includes(fleet.id)
                                                    ? "rotate-0"
                                                    : "-rotate-90",
                                            )}
                                        />
                                        {fleet.devicesStatus}
                                    </div>
                                )}
                            </CollapsibleTrigger>
                        </div>
                    </div>
                    {!!fleet.badge?.count && (
                        <div className="self-start">
                            <Badge
                                variant={fleet.badge.variant}
                                className={cn("font-normal rounded-full")}
                            >
                                {fleet.badge.count.toLocaleString()}
                            </Badge>
                        </div>
                    )}
                    {fleet.badgeContent}
                </div>

                <CollapsibleContent>
                    <List
                        height={calculateListHeight(
                            fleet.devices.length,
                            itemHeight,
                        )}
                        itemCount={fleet.devices.length}
                        itemSize={itemHeight}
                        width="100%"
                        itemData={{ devices: fleet.devices, navigateAndClose }}
                    >
                        {DeviceItem}
                    </List>
                </CollapsibleContent>
            </Collapsible>
        );
    };

    return (
        <SidebarContent
            className="px-2 py-4 bg-formant-color-background-secondary"
            ref={sidebarRef}
        >
            {fleetList.map(fleet => {
                return renderFleet(fleet);
            })}

            {groupedFleetList.map(item => (
                <Collapsible
                    className={` bg-formant-color-background-primary ${
                        !openGroups.includes(item.name)
                            ? "hover:bg-formant-color-background-active-2"
                            : ""
                    } collapsible !cursor-pointer rounded-lg`}
                    key={item.name}
                    open={openGroups.includes(item.name)}
                    onOpenChange={isOpen =>
                        setOpenGroups(
                            isOpen
                                ? [...openGroups, item.name]
                                : openGroups.filter(name => name !== item.name),
                        )
                    }
                >
                    <CollapsibleTrigger asChild={true}>
                        <div className="flex w-full items-center justify-between rounded-lg p-2 ">
                            <div>
                                <div className="flex flex-col cursor-pointer">
                                    <div className="flex items-center gap-2 text-formant-color-text-primary">
                                        {item.name}
                                    </div>

                                    <div className="flex items-center gap-1 text-sm  text-formant-color-text-primary  hover:bg-formant-color-background-active rounded-lg p-1">
                                        <ChevronDown
                                            className={cn(
                                                "h-4 w-4 transition-transform duration-200",
                                                openGroups.includes(item.name)
                                                    ? "rotate-0"
                                                    : "-rotate-90",
                                            )}
                                        />
                                        {item.data.length} Group
                                        {item.data.length > 1 ? "s" : ""}
                                    </div>
                                </div>
                            </div>
                            {!!item.badge.count && (
                                <Badge
                                    variant={item.badge.variant}
                                    className="font-normal rounded-full"
                                >
                                    {item.badge.count.toLocaleString()}
                                </Badge>
                            )}
                        </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="px-2">
                        {item.data.map(fleet => renderFleet(fleet, true))}
                    </CollapsibleContent>
                </Collapsible>
            ))}
        </SidebarContent>
    );
};
